/**
 * Apollo Client factory for veterans/shop (Vite SPA).
 *
 * Purpose: Creates an ApolloClient connected to the Hasura GraphQL endpoint.
 *   Credentials are sent via httpOnly cookie (vet-token) managed by the server;
 *   no bearer token is read from localStorage or any browser storage.
 * Inputs: None (reads env at call time)
 * Outputs: ApolloClient instance
 * Constraints: Client-side only; never runs on server.
 *   Privileged ops (service role) MUST go through api/* server handlers, never
 *   through this client. See hasura.ts for the server-side helper.
 * SPORT: veterans/.claude/docs/MASTER-COMPONENTS.md § ApolloClient
 *
 * Security: JWT_STORAGE_KEY / localStorage bearer tokens were removed (XSS
 *   vector). Browser sends credentials via withCredentials=true so the httpOnly
 *   vet-token cookie is included automatically on same-origin Hasura requests.
 *   Privileged shop ops use the service role server-side via api/* handlers only.
 */
import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client'

/**
 * Creates a new ApolloClient instance.
 * Use the singleton export `apolloClient` for the app; call this only for tests.
 */
export function createApolloClient(): ApolloClient<object> {
  // In Vite/browser context use import.meta.env; in Node/Vitest use process.env as fallback.
  // Both paths read the same var name (VITE_HASURA_GRAPHQL_URL). The test setup stubs the
  // process.env value so tests don't crash on module init. Production always uses import.meta.env.
  const uri =
    (import.meta.env?.VITE_HASURA_GRAPHQL_URL as string | undefined) ??
    (typeof process !== 'undefined' ? process.env['VITE_HASURA_GRAPHQL_URL'] : undefined)
  if (!uri) throw new Error('[veterans/shop] VITE_HASURA_GRAPHQL_URL is required but not set')
  const httpLink = new HttpLink({
    uri,
    // Send the httpOnly vet-token cookie on cross-origin requests to the
    // Hasura endpoint so authenticated vet_app* queries work without
    // exposing the token to JS.
    credentials: 'include',
  })
  return new ApolloClient({
    link: httpLink,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { fetchPolicy: 'cache-and-network' },
    },
  })
}

export const apolloClient = createApolloClient()
